import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {B2BMetricsRepository} from '../repositories/B2BMetricsRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {AdminCodesFilterParams, AdminCodeType, AdminTourOperatorFilter} from '../domain/entities/AdminCode.ts'

const b2bMetricsRepository = new B2BMetricsRepository(new AuthenticationRepository())

export function useAdminCodes(filter: AdminCodesFilterParams) {
    const query = useQuery({
        queryKey: ['admin-codes', filter],
        queryFn: () => b2bMetricsRepository.getAdminCodes(filter),
        placeholderData: keepPreviousData,
    })

    return {
        codes: query.data?.codes ?? [],
        total: query.data?.total ?? 0,
        isLoadingCodes: query.isLoading,
        isFetchingCodes: query.isFetching,
        codesError: query.error,
    }
}

// Independent of page/search so the KPI strip doesn't refetch on every keystroke.
// It does take `type`, though: the pills are buttons that filter the table, so
// counting over a different universe than the list would make the number on the
// pill disagree with the rows you get after clicking it.
export function useAdminCodesSummary(
    agencyIds: string[],
    tourOperator: AdminTourOperatorFilter,
    type: AdminCodeType,
) {
    const query = useQuery({
        queryKey: ['admin-codes-summary', agencyIds, tourOperator, type],
        queryFn: () => b2bMetricsRepository.getAdminCodesSummary(agencyIds, tourOperator, type),
    })

    return {
        steps: query.data?.steps ?? [],
        isLoadingSummary: query.isLoading,
        summaryError: query.error,
    }
}

// orderId acota los hitos del proveedor al pedido que se abrió: un código
// genérico tiene N pedidos, y sin él el panel de una reimpresión mostraría
// además los de las otras. Va en la queryKey porque dos filas del mismo
// código devuelven timelines distintos y no pueden compartir caché.
export function useAdminCodeHistory(codeId: string | null, orderId: string | null) {
    const query = useQuery({
        queryKey: ['admin-code-history', codeId, orderId],
        queryFn: () => b2bMetricsRepository.getAdminCodeHistory(codeId!, orderId),
        enabled: !!codeId,
    })

    return {
        entries: query.data?.entries ?? [],
        isLoadingHistory: query.isLoading,
        historyError: query.error,
    }
}
